import React from "react";

import { LegalMode } from "hassibot/services_v2/common/types";

type AppModule = { default: React.ComponentType<any> };
type AppImporter = () => Promise<AppModule>;

export type LaunchableApp = React.LazyExoticComponent<React.ComponentType<any>>;

type AppEntry = {
  // Path prefix used to match the current location (dispatcher) and to prefetch
  // the app chunk.
  matchPrefix: string;
  importer: AppImporter;
  // Set for apps whose route is declared centrally instead of an outer <Route>
  // in their index: the dispatcher mounts them through a legalModes-gated Route.
  // `routePath` defaults to `matchPrefix` when the route takes no extra params.
  //
  // Invariant: every path that selects this app via `matchPrefix` must also
  // match `routePath`, otherwise the dispatcher mounts a Route that renders
  // nothing. Make extra params optional (`:id?`) so the bare prefix still
  // matches; a path that can't match `routePath` is surfaced as a 404 (see
  // startup/main.tsx) rather than a silent blank page.
  routePath?: string;
  legalModes?: LegalMode[];
  // Forwarded to the central <Route>; set when the app only matches its exact
  // path (no sub-routes).
  exact?: boolean;
};

const MANDATARY_AND_CONTRACTOR = [LegalMode.MANDATARY, LegalMode.CONTRACTOR];
const MANDATARY_ONLY = [LegalMode.MANDATARY];

const appEntries: AppEntry[] = [
  {
    matchPrefix: "/ia/caregivers",
    importer: () => import("hassibot/apps/caregivers"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/dashboards",
    importer: () => import("hassibot/apps/dashboards"),
    routePath: "/ia/dashboards/",
    legalModes: MANDATARY_ONLY,
  },
  {
    matchPrefix: "/ia/prescribers",
    importer: () => import("hassibot/apps/prescribers"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/proclients",
    importer: () => import("hassibot/apps/proclients"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/notifications",
    importer: () => import("hassibot/apps/notifications"),
    routePath: "/ia/notifications/",
    exact: true,
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/chat",
    importer: () => import("hassibot/apps/chat"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/pico",
    importer: () => import("hassibot/apps/pico"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/collaborators",
    importer: () => import("hassibot/apps/collaborators"),
    routePath: "/ia/collaborators/:collaboratorGoogleID?",
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  { matchPrefix: "/ia/debug", importer: () => import("hassibot/apps/debug") },
  { matchPrefix: "/ia/test-error", importer: () => import("hassibot/apps/test-errors") },
  {
    matchPrefix: "/ia/upload-retry",
    importer: () => import("hassibot/apps/upload-retry"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  { matchPrefix: "/ia/cgpcs/contracts", importer: () => import("hassibot/apps/cgpc/contract") },
  { matchPrefix: "/ia/cgpcs-relations", importer: () => import("hassibot/apps/cgpc/relation") },
  {
    matchPrefix: "/ia/avs-search",
    importer: () => import("hassibot/apps/caregivers-search-V2"),
    legalModes: MANDATARY_ONLY,
  },
  {
    matchPrefix: "/ia/caregiver-search",
    importer: () => import("hassibot/apps/caregivers-search-V2"),
    legalModes: MANDATARY_ONLY,
  },
  {
    matchPrefix: "/ia/compass",
    importer: () => import("hassibot/apps/compass"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/dashboard/call-evaluations",
    importer: () => import("hassibot/apps/dashboard-inside-sales"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  { matchPrefix: "/ia/design-system", importer: () => import("hassibot/apps/design-system") },
  {
    matchPrefix: "/ia/monet",
    importer: () => import("hassibot/apps/monet"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/postal-codes",
    importer: () => import("hassibot/apps/postal-codes"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/permer",
    importer: () => import("hassibot/apps/permer"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/admin",
    importer: () => import("hassibot/apps/admin"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  { matchPrefix: "/ia/calls-evaluations", importer: () => import("hassibot/apps/call-interface") },
  {
    matchPrefix: "/ia/vebs-evaluations",
    importer: () => import("hassibot/apps/veb-interface"),
    routePath: "/ia/vebs-evaluations/:uuid",
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/documents/auth",
    importer: () => import("hassibot/apps/documents/auth"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/demo-calendar",
    importer: () => import("hassibot/apps/demo-calendar"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/metabase",
    importer: () => import("hassibot/apps/metabase"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/staffing",
    importer: () => import("hassibot/apps/staffing"),
    legalModes: MANDATARY_ONLY,
  },
  {
    matchPrefix: "/ia/retours-produit",
    importer: () => import("hassibot/apps/product-feedback"),
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  {
    matchPrefix: "/ia/engineering",
    importer: () => import("hassibot/apps/engineering"),
    routePath: "/ia/engineering/ai-error-reports",
    legalModes: MANDATARY_AND_CONTRACTOR,
  },
  { matchPrefix: "/ia/hassipote", importer: () => import("hassibot/apps/hassipote") },
  { matchPrefix: "/ia/phone", importer: () => import("hassibot/apps/phone-shell") },
];

export type Launcher = {
  matchPrefix: string;
  component: LaunchableApp;
  routePath: string;
  legalModes?: LegalMode[];
  exact?: boolean;
};

export const launchers: Launcher[] = appEntries.map(entry => ({
  matchPrefix: entry.matchPrefix,
  component: React.lazy(entry.importer),
  routePath: entry.routePath ?? entry.matchPrefix,
  legalModes: entry.legalModes,
  exact: entry.exact,
}));

/**
 * The app prefix that owns a pathname — same resolution as the dispatcher in
 * startup/main.tsx — or undefined for a path outside any app (e.g. "/").
 */
export const findAppMatchPrefix = (pathname: string): string | undefined =>
  launchers.find(({ matchPrefix }) => pathname.startsWith(matchPrefix))?.matchPrefix;

/**
 * True when navigating from one app to a *different* app. False when either side
 * is outside any app (e.g. a boot redirect from "/"), so those navigations stay
 * in-SPA.
 */
export const isInterAppNavigation = (fromPathname: string, toPathname: string): boolean => {
  const fromPrefix = findAppMatchPrefix(fromPathname);
  const toPrefix = findAppMatchPrefix(toPathname);
  return fromPrefix !== undefined && toPrefix !== undefined && fromPrefix !== toPrefix;
};

const prefetchedPaths = new Set<string>();

const prefetchEntry = (entry: AppEntry): Promise<unknown> => {
  if (prefetchedPaths.has(entry.matchPrefix)) {
    return Promise.resolve();
  }
  prefetchedPaths.add(entry.matchPrefix);
  return entry.importer().catch(() => {
    prefetchedPaths.delete(entry.matchPrefix);
  });
};

/**
 * Start downloading (and evaluating) the chunk of the app matching the given
 * path, typically on navbar link hover: by the time the user clicks, the
 * lazy component usually has nothing left to load.
 */
export const prefetchAppForPath = (path: string): void => {
  const entry = appEntries.find(({ matchPrefix }) => path.startsWith(matchPrefix));
  if (entry) {
    prefetchEntry(entry);
  }
};

const whenIdle = (callback: () => void): void => {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(callback, { timeout: 10_000 });
  } else {
    window.setTimeout(callback, 2_000);
  }
};

let allAppsPrefetchStarted = false;

/**
 * Warm up every app chunk during browser idle time, one app at a time, so the
 * first navigation to any app has nothing left to load. Called once the shell
 * is up and the main cache data is fetched, to stay off the critical path.
 */
export const prefetchAllAppsWhenIdle = (): void => {
  if (allAppsPrefetchStarted) {
    return;
  }
  allAppsPrefetchStarted = true;
  const queue = [...appEntries];
  const next = (): void => {
    const entry = queue.shift();
    if (!entry) {
      return;
    }
    prefetchEntry(entry).finally(() => whenIdle(next));
  };
  whenIdle(next);
};
